import { useLingui } from '@lingui/react/macro';

/**
 * A point on the timeline. `caption` and `image` are OPTIONAL because the hand-off only spells out the
 * three milestones that fall inside its viewport — 2012, 2014 and 2017 — while the year rail lists
 * seven. The remaining four are drawn but never labelled, and no photograph is exported for any of
 * them (`background: url(.jpg)` in the CSS is an empty reference).
 *
 * A slide with no copy renders the placeholder and no caption, on purpose: an invented milestone would
 * be worse than a visibly unfinished one. Fill these in and the section is done.
 */
/**
 * A milestone's artwork, in the two shapes the hand-off uses — the same `kind` discriminator the
 * Solutions cards use, for the same reason: the two are rendered completely differently.
 *
 * `photo` FILLS the card, cropped. Its `src` is either one file or one per locale keyed by language with
 * `en` as the required fallback: 2025's is a phone showing the app, so its screen has to be in the
 * reader's language. That per-locale idiom is now in three places in this route group (the app-download
 * band and the solution tabs are the others) and is worth hoisting when a fourth appears.
 *
 * `illustration` sits ON the plate instead, CENTRED and nudged 9px above the card's middle, as the
 * export places it. Each one carries its own `width` because the two drawings are not the same shape —
 * the globe is near-square at 312x316, the terminal portrait at 255x318 — so a shared width would have
 * made one of them overflow. The globe's is the export's own; the terminal's is set so it overhangs the
 * card's 318px height by the same amount.
 *
 * `screen` is a product capture, and it is placed the other way round: the plate goes DARK and the
 * capture is anchored to the card's bottom-RIGHT corner so it bleeds off two edges instead of sitting
 * centred with air around it. That was read off the design rather than an export: the dark plate carries
 * the card's own aspect (1.403 against 1.390) and the capture the SVG's (1.431 against 1.440), which is
 * what identifies the dark shape as the CARD and not a device bezel drawn into the artwork.
 */
export type MilestoneArt =
	| { kind: 'photo'; src: string | (Record<string, string> & { en: string }); alt: string }
	| { kind: 'illustration'; src: string; alt: string; width: string }
	| { kind: 'screen'; src: string; alt: string; width: string };

/**
 * `id` exists because `year` is NOT unique — 2025 holds two milestones — so it can no longer key the
 * list. It is deliberately not the caption: that is a translated string, and keying on it would remount
 * every slide on a language change.
 */
export type Milestone = { id: string; year: string; caption?: string; image?: MilestoneArt };

/** A hook, not a constant: `t` only extracts and only re-runs on a language change inside a component. */
export function useMilestones(): Milestone[] {
	const { t } = useLingui();
	return [
		{
			id: 'born',
			year: '2012',
			caption: t`We were born in Barcelona`,
			image: { kind: 'photo', src: '/redesign/about/barcelona-skyline.jpg', alt: t`Barcelona seen from above` },
		},
		{
			id: 'ecommerce',
			year: '2014',
			caption: t`We launched the Solidarity Euro for ecommerce`,
			image: { kind: 'photo', src: '/redesign/about/laptop-checkout.jpg', alt: t`Someone shopping online on a laptop` },
		},
		{
			id: 'stores',
			year: '2017',
			caption: t`We moved into physical stores with Charity Rounding Up`,
			image: {
				kind: 'photo',
				src: '/redesign/about/contactless-terminal.jpg',
				alt: t`A shopper paying by contactless card at a shop counter`,
			},
		},
		{
			id: 'million',
			year: '2018',
			caption: t`We reached one million euros raised`,
			image: {
				kind: 'illustration',
				src: '/redesign/about/terminal-coins.svg',
				alt: t`An illustration of euro coins dropping into a card terminal`,
				width: '60.2%', // 266 of 442: the height that overhangs as much as the globe does
			},
		},
		{
			id: 'expansion',
			year: '2022',
			caption: t`We expanded into Andorra and Portugal`,
			image: {
				kind: 'illustration',
				src: '/redesign/about/globe-hearts.svg',
				alt: t`An illustration of the globe with two hearts over Europe`,
				width: '70.6%', // the export's own 312 of 442
			},
		},
		{
			id: 'app',
			year: '2025',
			caption: t`We launched our app for donors`,
			// PER-LOCALE, and only `en` exists so far: the other four fall back to it until the captures
			// arrive. Reuses the alt the Solutions page already ships for the same screen.
			image: {
				kind: 'photo',
				src: { en: '/redesign/about/thanks-screen-en.jpg' },
				alt: t`The Worldcoo app thanking a donor for their donation`,
			},
		},
		{
			id: 'italy',
			// The SECOND 2025. The design gives that year two cards, which is why the rail below is a
			// year axis rather than a slide list.
			year: '2025',
			caption: t`We achieved our first success story in Italy`,
			image: {
				kind: 'photo',
				src: '/redesign/about/italy-flag.jpg',
				alt: t`The Italian flag flying against a blue sky`,
			},
		},
		{
			id: 'plus',
			year: '2026',
			caption: t`We launched Worldcoo Plus, a new service for NGOs`,
			image: {
				kind: 'screen',
				src: '/redesign/about/plus-dashboard.svg',
				alt: t`The Worldcoo Plus dashboard for NGOs`,
				width: '84.3%', // measured off the design: 458 of the card's 543
			},
		},
	];
}
